import moment from 'moment-timezone';
import orderBy from 'lodash/orderBy';
import isNumber from 'lodash/isNumber';
import { getDomesticCupCalendar } from './utils';

export const eventKey = (event) =>
  `${event.type}-${event.team?._id || event.team?.name}-${moment.utc(event.date).format('YYYY-MM-DD')}`;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

export const gamesFromFixtures = (calendar, teamId, afterDate, afterOpponentId) => {
  if (!teamId) return [];
  const days = Array.isArray(calendar?.matches) ? calendar.matches : [];
  const games = [];
  days.forEach((day) => {
    const nested = Array.isArray(day?.matches)
      ? day.matches
      : day?.home_team
        ? [day]
        : [];
    nested.forEach((match) => {
      if (!match?.home_team || !match?.away_team) return;
      const userIsHome = sameId(match.home_team._id, teamId);
      const userIsAway = sameId(match.away_team._id, teamId);
      if (!userIsHome && !userIsAway) return;
      const date = match.match_date || day.match_date || day.date;
      const opponent = userIsHome ? match.away_team : match.home_team;
      if (afterDate && moment(date).isBefore(moment(afterDate), 'day')) return;
      if (
        afterDate &&
        moment(date).isSame(moment(afterDate), 'day') &&
        (!afterOpponentId || sameId(opponent._id, afterOpponentId))
      ) {
        return;
      }
      if (
        !afterDate &&
        isNumber(match.home_goals) && match.home_goals >= 0 &&
        isNumber(match.away_goals) && match.away_goals >= 0
      ) {
        return;
      }
      const matchId = match._id || match.id || match.match_id || null;
      games.push({
        date,
        team: opponent,
        type: match.domestic_cup ? 1 : 0,
        matchId: matchId || undefined,
        homeId: match.home_team._id,
        awayId: match.away_team._id,
        homeName: match.home_team.name,
        awayName: match.away_team.name,
        homeSymbol: match.home_team.symbol,
        awaySymbol: match.away_team.symbol,
      });
    });
  });
  return orderBy(games, ['date'], ['asc']);
};

export const gamesFromStrategy = (data, afterDate, afterOpponentId) => {
  if (!Array.isArray(data)) return [];
  const sorted = orderBy(data.filter((event) => event.date && moment.utc(event.date).isValid()),
    [(event) => moment.utc(event.date).valueOf()], ['asc']);
  if (!afterDate) return sorted;
  return sorted.filter((event) => {
    const eventDay = moment.utc(event.date);
    const nextDay = moment.utc(afterDate);
    if (eventDay.isBefore(nextDay, 'day')) return false;
    return !eventDay.isSame(nextDay, 'day') ||
      (afterOpponentId && !sameId(event.team?._id, afterOpponentId));
  });
};

// The cup endpoint groups matches by round rather than calendar day.
export const gamesFromCup = (cup, teamId) => {
  if (!teamId || !cup) return [];
  const rounds = ['qual_round', 'first_round_first_set', 'first_round_second_set', 'second_round', 'final_round'];
  return rounds.flatMap((round, phase) => (cup[round] || []).flatMap((match) => {
    const home = match.home_team;
    const away = match.away_team;
    if (!home?._id || !away?._id) return [];
    if (!sameId(home._id, teamId) && !sameId(away._id, teamId)) return [];
    const scored = (score) => typeof score === 'number' && score >= 0;
    if ((scored(home.score) && scored(away.score)) ||
        (scored(match.home_goals) && scored(match.away_goals))) return [];
    const date = match.match_date || (cup.startDate && getDomesticCupCalendar(cup.startDate, phase));
    if (!date || !moment.utc(date).isValid()) return [];
    const matchId = match._id || match.id || match.match_id || null;
    return [{
      date,
      team: sameId(home._id, teamId) ? away : home,
      type: 1,
      matchId: matchId || undefined,
      homeId: home._id,
      awayId: away._id,
      homeName: home.name,
      awayName: away.name,
      homeSymbol: home.symbol,
      awaySymbol: away.symbol,
    }];
  }));
};

export const mergeUpcomingGames = (fixtures, strategy, cup) => {
  // Cup fixtures override estimated strategy dates for the same opponent.
  const cupOpponents = new Set(cup.map((event) => String(event.team._id)));
  const result = [...cup];
  const seen = new Set(result.map(eventKey));
  [...fixtures, ...strategy].forEach((event) => {
    if (Number(event.type) === 1 && cupOpponents.has(String(event.team?._id))) return;
    const key = eventKey(event);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(event);
    }
  });
  return orderBy(result, [(event) => moment.utc(event.date).valueOf()], ['asc']);
};
